/**
 * Admin Artwork Control Center — safe self-service artwork UI.
 *
 * Search → inspect → refresh / replace / sync / reset. Every write is
 * audited and confirmed. Reuses the canonical getCoverUrl() resolver
 * for the preview image. Never substitutes an artist avatar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCoverUrl } from "@/lib/mediaUrls";

const STATUS_TONE = {
  healthy: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  stale_snapshot: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  missing: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  profile_photo_detected: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40",
  source_deleted: "bg-rose-500/20 text-rose-200 border-rose-500/40",
};

const SOURCE_LABEL = {
  track: "Track",
  beat: "Beat",
  contest_entry: "Contest Entry",
  hof: "Hall of Champions",
  spotlight_winner: "Spotlight Winner",
};

const AdminArtworkControlCenterPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);

  const search = useCallback(async () => {
    if ((query || "").length < 2) {
      setResults([]); return;
    }
    setSearching(true);
    try {
      const r = await api.get("/admin/artwork/search",
        { params: { q: query, limit: 25 } });
      setResults(r.data?.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  }, [query]);

  const loadInspect = useCallback(async (row) => {
    setSelected(row);
    setInspect(null);
    setPreviewRows([]);
    try {
      const r = await api.get(`/admin/artwork/inspect/${row.source_type}/${encodeURIComponent(row.source_id)}`);
      setInspect(r.data);
      const p = await api.get(`/admin/artwork/sync-preview/${row.source_type}/${encodeURIComponent(row.source_id)}`);
      setPreviewRows(p.data?.affected || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inspect failed");
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/admin/artwork/audit", { params: { limit: 15 } });
      setAudit(r.data?.rows || []);
    } catch (_e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const refresh = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Refresh ${SOURCE_LABEL[selected.source_type]} ${selected.source_id} from canonical source?\n\n` +
      `This re-resolves the artwork reference from the owning track/beat.\n` +
      `• Audit log: records admin, timestamp, previous and new URLs.\n` +
      `• Scores/winners/rewards: NOT touched.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/artwork/refresh/${selected.source_type}/${encodeURIComponent(selected.source_id)}`);
      toast.success("Refreshed from canonical source");
      await loadInspect(selected);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setBusy(false); }
  };

  const uploadReplacement = async (file) => {
    if (!selected || !file) return;
    if (!window.confirm(
      `Replace artwork on ${SOURCE_LABEL[selected.source_type]} ${selected.source_id} with the uploaded image?\n\n` +
      `• Audio, ownership, artist identity, scores, winners, rewards: NOT touched.`
    )) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/artwork/replace/${selected.source_type}/${encodeURIComponent(selected.source_id)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Artwork replaced");
      await loadInspect(selected);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replace failed");
    } finally { setBusy(false); }
  };

  const sync = async () => {
    if (!selected) return;
    const n = previewRows.length;
    if (n === 0) {
      toast.message("No downstream snapshots to sync");
      return;
    }
    if (!window.confirm(
      `Push canonical artwork to ${n} downstream snapshot${n === 1 ? "" : "s"}?\n\n` +
      previewRows.slice(0, 10).map(a => `• ${a.surface} ${a.id}`).join("\n") +
      (n > 10 ? `\n…and ${n - 10} more` : "") +
      "\n\nSpotlight placements / rewards / scores: NOT touched."
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/artwork/sync/${selected.source_type}/${encodeURIComponent(selected.source_id)}`,
        { surfaces: null });
      toast.success(`Synced ${r.data?.count ?? 0} snapshots`);
      await loadInspect(selected);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Reset the stored artwork reference on ${SOURCE_LABEL[selected.source_type]} ${selected.source_id}?\n\n` +
      `The branded RGM 'cover unavailable' placeholder will be used.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/artwork/reset/${selected.source_type}/${encodeURIComponent(selected.source_id)}`);
      toast.success("Artwork reference cleared");
      await loadInspect(selected);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const renderedCover = useMemo(() => {
    if (!inspect) return getCoverUrl(null);
    return getCoverUrl({ cover_url: inspect.stored_url || inspect.canonical_url });
  }, [inspect]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-artwork-control-center-page">
      <SharedNavigation />
      <div className="max-w-5xl mx-auto pt-24 px-4 pb-20 space-y-6">
        <div>
          <Link to="/admin" className="text-xs text-purple-300 hover:underline" data-testid="admin-artwork-back">← Admin</Link>
          <h1 className="text-3xl sm:text-4xl font-black mt-1" data-testid="admin-artwork-title">
            🎨 Artwork Control Center
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Search any track, beat, contest entry, Spotlight winner, or Hall record. Inspect the canonical cover and apply a single safe action. Every change is audited.
          </p>
        </div>

        {/* 1. Search */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Search</p>
          <div className="flex gap-2">
            <Input
              value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              placeholder="Search by title, artist, or ID…"
              className="bg-black/40 border-white/10 text-white"
              data-testid="admin-artwork-search-input"
            />
            <Button onClick={search} disabled={searching} data-testid="admin-artwork-search-btn">
              {searching ? "…" : "Search"}
            </Button>
          </div>
          {results.length > 0 && (
            <ul className="mt-3 space-y-1 max-h-64 overflow-y-auto" data-testid="admin-artwork-results">
              {results.map((r) => (
                <li key={`${r.source_type}:${r.source_id}`}>
                  <button
                    type="button"
                    onClick={() => loadInspect(r)}
                    className={`w-full text-left flex items-center gap-2 p-2 rounded hover:bg-white/5 ${selected?.source_id === r.source_id ? "bg-fuchsia-500/10" : ""}`}
                    data-testid={`admin-artwork-result-${r.source_type}-${r.source_id}`}
                  >
                    <img src={getCoverUrl({ cover_url: r.cover_url })} alt=""
                         className="w-8 h-8 rounded object-cover" />
                    <span className="text-xs flex-1 truncate">
                      <span className="text-fuchsia-200">{SOURCE_LABEL[r.source_type]}</span>
                      {" · "}
                      <span className="text-white">{r.title || r.source_id}</span>
                    </span>
                    <span className="text-[10px] text-gray-500">{r.source_id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 2. Inspection */}
        {inspect && (
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="admin-artwork-inspect">
            <div className="flex items-start gap-4 flex-wrap">
              <img src={renderedCover} alt="Current cover" className="w-28 h-28 rounded-lg object-cover border border-white/10"
                   data-testid="admin-artwork-current-cover" />
              <div className="min-w-0 flex-1 space-y-1 text-xs">
                <p><span className="text-gray-400">Source type:</span> <span className="text-white font-black">{SOURCE_LABEL[inspect.source_type]}</span></p>
                <p><span className="text-gray-400">Source ID:</span> <code className="text-fuchsia-200">{inspect.source_id}</code></p>
                <p><span className="text-gray-400">Status:</span>{" "}
                  <span className={`inline-block px-2 py-0.5 rounded-full border font-black ${STATUS_TONE[inspect.status] || "bg-white/5 text-gray-300"}`}
                        data-testid="admin-artwork-status">
                    {inspect.status.replace(/_/g, " ").toUpperCase()}
                  </span>
                </p>
                <p className="break-all"><span className="text-gray-400">Stored URL:</span> <span className="text-white">{inspect.stored_url || "— none —"}</span></p>
                <p className="break-all"><span className="text-gray-400">Canonical URL:</span> <span className="text-white">{inspect.canonical_url || "— none —"}</span></p>
                <p><span className="text-gray-400">Original source exists:</span> <span className="text-white font-black">{inspect.source_exists ? "yes" : "NO (deleted)"}</span></p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <Button
                onClick={refresh}
                disabled={busy || !["contest_entry", "hof"].includes(inspect.source_type) || !inspect.canonical_url}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs"
                data-testid="admin-artwork-refresh-btn"
                title={!inspect.canonical_url ? "No canonical cover available" : ""}
              >
                🔄 Refresh From Original
              </Button>
              <label className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white font-black text-xs rounded px-3 py-2 cursor-pointer"
                     data-testid="admin-artwork-replace-label">
                📤 Replace Artwork
                <input
                  type="file" accept="image/*" className="hidden"
                  disabled={busy}
                  onChange={(e) => uploadReplacement(e.target.files?.[0])}
                  data-testid="admin-artwork-replace-input"
                />
              </label>
              <Button
                onClick={sync}
                disabled={busy || !inspect.canonical_url || previewRows.length === 0}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-black text-xs"
                data-testid="admin-artwork-sync-btn"
              >
                🔗 Sync Everywhere ({previewRows.length})
              </Button>
              <Button
                onClick={reset}
                disabled={busy || !["contest_entry", "hof"].includes(inspect.source_type)}
                variant="outline"
                className="text-amber-200 border-amber-500/40 hover:bg-amber-500/10 font-black text-xs"
                data-testid="admin-artwork-reset-btn"
              >
                ♻️ Reset to Placeholder
              </Button>
            </div>

            {previewRows.length > 0 && (
              <details className="mt-4" data-testid="admin-artwork-preview">
                <summary className="text-[10px] uppercase tracking-widest text-gray-400 cursor-pointer">
                  Sync preview · {previewRows.length} downstream snapshot{previewRows.length === 1 ? "" : "s"}
                </summary>
                <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto text-[11px]">
                  {previewRows.map((a) => (
                    <li key={`${a.surface}:${a.id}`} className="flex items-center gap-2 p-1 rounded bg-white/[0.02]">
                      <span className="text-fuchsia-200 w-24 shrink-0">{a.surface}</span>
                      <code className="text-white truncate flex-1">{a.id}</code>
                      <span className="text-gray-500 truncate max-w-[12rem]">{a.context || ""}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}

        {/* 3. Audit log */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="admin-artwork-audit-section">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Recent audit · last 15</p>
          {audit.length === 0 ? (
            <p className="text-xs text-gray-500">No admin artwork actions yet.</p>
          ) : (
            <ul className="space-y-1 text-[11px]">
              {audit.map((a) => (
                <li key={a.audit_id} className="flex items-center gap-2 p-1 rounded bg-white/[0.02]"
                    data-testid={`admin-artwork-audit-${a.audit_id}`}>
                  <span className="text-fuchsia-200 w-16 shrink-0">{a.action}</span>
                  <span className="text-gray-400 w-28 shrink-0">{SOURCE_LABEL[a.source_type] || a.source_type}</span>
                  <code className="text-white truncate flex-1">{a.source_id}</code>
                  <span className="text-gray-500 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminArtworkControlCenterPage;
